import { useEffect, useRef, useState } from "react";
import { AUDIO_PARAMETERS, PARAMETER_BY_ID, VIDEO_PARAMETERS } from "../model/catalog";
import type { ScopeCanvases } from "../engine";
import type { CRTVarPatch, ModulationRoute, ModulationSource, ParameterId, RuntimeState, Waveform } from "../model/types";
import { useAppStore } from "../state/store";
import { Dial, Meter, SelectField, Toggle } from "./Controls";

const modSources: Array<[ModulationSource, string]> = [
  ["lfo1", "LFO 1"], ["lfo2", "LFO 2"], ["envelope", "Synth envelope"], ["notePitch", "Note pitch"],
  ["noteVelocity", "Note velocity"], ["audioRms", "Audio RMS"], ["audioLow", "Low band"], ["audioMid", "Mid band"],
  ["audioHigh", "High band"], ["noise", "Seeded noise"], ["sequencer", "Sequencer step"], ["expression", "Expression"],
];

const getValue = (patch: CRTVarPatch, id: ParameterId): number => {
  const [section, key] = id.split(".") as ["video" | "synth", string];
  if (section === "video") return Number((patch.video as unknown as Record<string, unknown>)[key] ?? 0);
  if (key.startsWith("osc1")) {
    const child = key.replace("osc1", "");
    return Number((patch.synth.osc1 as unknown as Record<string, unknown>)[`${child.charAt(0).toLowerCase()}${child.slice(1)}`] ?? 0);
  }
  if (key.startsWith("osc2")) {
    const child = key.replace("osc2", "");
    return Number((patch.synth.osc2 as unknown as Record<string, unknown>)[`${child.charAt(0).toLowerCase()}${child.slice(1)}`] ?? 0);
  }
  return Number((patch.synth as unknown as Record<string, unknown>)[key] ?? 0);
};

export function VideoPanel({ patch, onParameter, onPatch }: {
  patch: CRTVarPatch;
  onParameter: (id: ParameterId, value: number) => void;
  onPatch: (updater: (patch: CRTVarPatch) => CRTVarPatch) => void;
}) {
  return (
    <div className="panel-grid">
      <section className="module module--wide">
        <header><span>VIDEO INTERFERENCE</span><small>IMAGE + COMPOSITE TARGETS</small></header>
        <div className="dial-grid dial-grid--video">
          {VIDEO_PARAMETERS.map((descriptor) => <Dial key={descriptor.id} descriptor={descriptor} value={getValue(patch, descriptor.id)} onChange={(value) => onParameter(descriptor.id, value)} />)}
        </div>
      </section>
      <section className="module">
        <header><span>RENDERER</span><small>PIPELINE</small></header>
        <div className="segmented" role="group" aria-label="Video engine mode">
          <button type="button" aria-pressed={patch.video.mode === "image"} onClick={() => onPatch((next) => ({ ...next, video: { ...next.video, mode: "image" } }))}>IMAGE SPACE</button>
          <button type="button" aria-pressed={patch.video.mode === "composite"} onClick={() => onPatch((next) => ({ ...next, video: { ...next.video, mode: "composite" } }))}>NTSC MODEL</button>
        </div>
        <dl className="signal-spec">
          <div><dt>Raster</dt><dd>{patch.video.mode === "composite" ? "910 × 525" : "640 × 480"}</dd></div>
          <div><dt>Cadence</dt><dd>{patch.video.mode === "composite" ? "59.94 fields" : "display sync"}</dd></div>
          <div><dt>Color</dt><dd>YIQ interference</dd></div>
          <div><dt>Temporal</dt><dd>ping-pong buffer</dd></div>
        </dl>
      </section>
    </div>
  );
}

function OscillatorModule({ index, patch, onPatch }: { index: 1 | 2; patch: CRTVarPatch; onPatch: (updater: (patch: CRTVarPatch) => CRTVarPatch) => void }) {
  const oscillator = index === 1 ? patch.synth.osc1 : patch.synth.osc2;
  const change = <K extends keyof typeof oscillator>(key: K, value: (typeof oscillator)[K]) => onPatch((next) => {
    const synth = structuredClone(next.synth);
    const target = index === 1 ? synth.osc1 : synth.osc2;
    target[key] = value;
    return { ...next, synth };
  });
  return (
    <section className="module">
      <header><span>OSCILLATOR {index}</span><small>{index === 1 ? "PRIMARY" : "SUB / DETUNE"}</small></header>
      <SelectField label="Waveform" value={oscillator.waveform} onChange={(event) => change("waveform", event.currentTarget.value as Waveform)}>
        <option value="sine">Sine</option><option value="triangle">Triangle</option><option value="saw">Saw</option><option value="square">Square</option>
      </SelectField>
      <label className="numeric-field"><span>Octave</span><input type="number" min={-3} max={3} value={oscillator.octave} onChange={(event) => change("octave", Math.max(-3, Math.min(3, Number(event.currentTarget.value))))} /></label>
      <div className="dial-grid dial-grid--small">
        {[`synth.osc${index}Detune`, `synth.osc${index}Level`].map((id) => {
          const descriptor = PARAMETER_BY_ID.get(id as ParameterId)!;
          return <Dial compact key={id} descriptor={descriptor} value={getValue(patch, id as ParameterId)} onChange={(value) => change(id.endsWith("Level") ? "level" : "detune", value)} />;
        })}
      </div>
    </section>
  );
}

export function SynthPanel({ patch, onParameter, onPatch }: {
  patch: CRTVarPatch;
  onParameter: (id: ParameterId, value: number) => void;
  onPatch: (updater: (patch: CRTVarPatch) => CRTVarPatch) => void;
}) {
  const remaining = AUDIO_PARAMETERS.filter((item) => !item.id.includes("osc1") && !item.id.includes("osc2"));
  return (
    <div className="panel-grid panel-grid--synth">
      <OscillatorModule index={1} patch={patch} onPatch={onPatch} />
      <OscillatorModule index={2} patch={patch} onPatch={onPatch} />
      <section className="module module--wide">
        <header><span>FILTER / ENVELOPE / DRIVE</span><small>FOUR VOICES · OLDEST-NOTE STEALING</small></header>
        <div className="dial-grid">{remaining.map((descriptor) => <Dial key={descriptor.id} descriptor={descriptor} value={getValue(patch, descriptor.id)} onChange={(value) => onParameter(descriptor.id, value)} />)}</div>
      </section>
    </div>
  );
}

export function ModulationPanel({ patch, onPatch }: { patch: CRTVarPatch; onPatch: (updater: (patch: CRTVarPatch) => CRTVarPatch) => void }) {
  const mutateRoute = (id: string, update: Partial<ModulationRoute>) => onPatch((next) => ({ ...next, modulationRoutes: next.modulationRoutes.map((route) => route.id === id ? { ...route, ...update } : route) }));
  const add = () => onPatch((next) => next.modulationRoutes.length >= 8 ? next : ({
    ...next,
    modulationRoutes: [...next.modulationRoutes, { id: `route-${Date.now().toString(36)}`, source: "lfo1", target: "video.horizontalJitter", depth: 0, smoothingMs: 40, enabled: true }],
  }));
  return (
    <section className="module module--wide modulation-module">
      <header><span>MODULATION MATRIX</span><small>{patch.modulationRoutes.length}/8 ROUTES · NORMALIZED −1…+1</small></header>
      <div className="mod-matrix" role="table" aria-label="Modulation routes">
        <div className="mod-row mod-row--head" role="row"><span>SOURCE</span><span>TARGET</span><span>DEPTH</span><span>SMOOTH</span><span>STATE</span><span /></div>
        {patch.modulationRoutes.map((route) => (
          <div className="mod-row" role="row" key={route.id}>
            <select aria-label="Modulation source" value={route.source} onChange={(event) => mutateRoute(route.id, { source: event.currentTarget.value as ModulationSource })}>{modSources.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
            <select aria-label="Modulation target" value={route.target} onChange={(event) => mutateRoute(route.id, { target: event.currentTarget.value as ParameterId })}>{[...PARAMETER_BY_ID.values()].map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select>
            <label><span className="sr-only">Depth</span><input type="range" min={-1} max={1} step={0.01} value={route.depth} onChange={(event) => mutateRoute(route.id, { depth: Number(event.currentTarget.value) })} /><output>{route.depth > 0 ? "+" : ""}{route.depth.toFixed(2)}</output></label>
            <label><span className="sr-only">Smoothing milliseconds</span><input type="number" min={0} max={5000} value={route.smoothingMs} onChange={(event) => mutateRoute(route.id, { smoothingMs: Number(event.currentTarget.value) })} /><small>ms</small></label>
            <Toggle pressed={route.enabled} onPressedChange={(enabled) => mutateRoute(route.id, { enabled })}>{route.enabled ? "ON" : "OFF"}</Toggle>
            <button className="text-button" type="button" onClick={() => onPatch((next) => ({ ...next, modulationRoutes: next.modulationRoutes.filter((item) => item.id !== route.id) }))}>REMOVE</button>
          </div>
        ))}
      </div>
      <button className="add-route" type="button" onClick={add} disabled={patch.modulationRoutes.length >= 8}>+ ADD ROUTE</button>
    </section>
  );
}

export function SequencerPanel({ patch, onPatch }: { patch: CRTVarPatch; onPatch: (updater: (patch: CRTVarPatch) => CRTVarPatch) => void }) {
  const [selectedStep, setSelectedStep] = useState(0);
  const activeStep = useAppStore((state) => state.runtime.telemetry.sequencerStep);
  const updateSettings = (update: Partial<CRTVarPatch["sequencer"]>) => onPatch((next) => ({ ...next, sequencer: { ...next.sequencer, ...update } }));
  const updateStep = (index: number, update: Partial<CRTVarPatch["sequencer"]["steps"][number]>) => onPatch((next) => ({
    ...next,
    sequencer: {
      ...next.sequencer,
      steps: next.sequencer.steps.map((step, stepIndex) => stepIndex === index ? { ...step, ...update } : step),
    },
  }));
  const selected = patch.sequencer.steps[selectedStep] ?? patch.sequencer.steps[0];
  return (
    <section className="module module--wide sequencer-module">
      <header><span>STEP SEQUENCER</span><small>WORKLET CLOCK · 1/16</small></header>
      <div className="sequencer-controls">
        <Toggle pressed={patch.sequencer.enabled} onPressedChange={(enabled) => updateSettings({ enabled })} tone="amber">{patch.sequencer.enabled ? "RUNNING" : "START"}</Toggle>
        <label className="numeric-field"><span>Tempo</span><input type="number" min={40} max={240} value={patch.sequencer.bpm} onChange={(event) => updateSettings({ bpm: Number(event.currentTarget.value) })} /><small>BPM</small></label>
        <label className="numeric-field"><span>Swing</span><input type="range" min={0} max={0.6} step={0.01} value={patch.sequencer.swing} onChange={(event) => updateSettings({ swing: Number(event.currentTarget.value) })} /><output>{Math.round(patch.sequencer.swing * 100)}%</output></label>
      </div>
      <div className="steps">
        {patch.sequencer.steps.map((step, index) => (
          <button
            type="button"
            key={index}
            className={`step ${patch.sequencer.enabled && activeStep === index ? "is-current" : ""}`}
            aria-pressed={step.enabled}
            aria-current={patch.sequencer.enabled && activeStep === index ? "step" : undefined}
            aria-label={`Step ${index + 1}: ${step.enabled ? "enabled" : "disabled"}, ${midiName(step.note)}, velocity ${Math.round(step.velocity * 100)} percent, gate ${Math.round(step.gate * 100)} percent${patch.sequencer.enabled && activeStep === index ? ", current step" : ""}`}
            onFocus={() => setSelectedStep(index)}
            onClick={() => {
              setSelectedStep(index);
              updateStep(index, { enabled: !step.enabled });
            }}
          ><span>{patch.sequencer.enabled && activeStep === index ? "▶" : String(index + 1).padStart(2, "0")}</span><strong>{step.enabled ? midiName(step.note) : "—"}</strong><small>{Math.round(step.velocity * 100)}</small></button>
        ))}
      </div>
      {selected && <div className="sequencer-controls" role="group" aria-label={`Edit sequencer step ${selectedStep + 1}`}>
        <strong>STEP {String(selectedStep + 1).padStart(2, "0")}</strong>
        <label className="numeric-field"><span>Note</span><input type="number" min={0} max={127} value={selected.note} onChange={(event) => updateStep(selectedStep, { note: Math.max(0, Math.min(127, Math.round(Number(event.currentTarget.value)))) })} /><small>{midiName(selected.note)}</small></label>
        <label className="numeric-field"><span>Velocity</span><input type="range" min={0} max={1} step={0.01} value={selected.velocity} onChange={(event) => updateStep(selectedStep, { velocity: Number(event.currentTarget.value) })} /><output>{Math.round(selected.velocity * 100)}%</output></label>
        <label className="numeric-field"><span>Gate</span><input type="range" min={0.05} max={1} step={0.01} value={selected.gate} onChange={(event) => updateStep(selectedStep, { gate: Number(event.currentTarget.value) })} /><output>{Math.round(selected.gate * 100)}%</output></label>
      </div>}
    </section>
  );
}

function midiName(note: number) {
  const names = ["C", "C♯", "D", "D♯", "E", "F", "F♯", "G", "G♯", "A", "A♯", "B"];
  return `${names[note % 12]}${Math.floor(note / 12) - 1}`;
}

function drawScope(canvas: HTMLCanvasElement, runtime: RuntimeState, kind: "wave" | "spectrum" | "composite") {
  const context = canvas.getContext("2d");
  if (!context) return;
  const width = canvas.width = Math.max(320, canvas.clientWidth * devicePixelRatio);
  const height = canvas.height = Math.max(120, canvas.clientHeight * devicePixelRatio);
  context.clearRect(0, 0, width, height);
  context.fillStyle = "#07100f";
  context.fillRect(0, 0, width, height);
  context.strokeStyle = "rgba(80, 190, 160, .13)";
  context.lineWidth = 1;
  for (let x = 0; x < width; x += width / 12) { context.beginPath(); context.moveTo(x, 0); context.lineTo(x, height); context.stroke(); }
  for (let y = 0; y < height; y += height / 6) { context.beginPath(); context.moveTo(0, y); context.lineTo(width, y); context.stroke(); }
  context.strokeStyle = kind === "composite" ? "#f4b858" : "#70e0c0";
  context.lineWidth = 2 * devicePixelRatio;
  context.shadowColor = context.strokeStyle;
  context.shadowBlur = 7 * devicePixelRatio;
  context.beginPath();
  if (kind === "spectrum") {
    const bands = [runtime.telemetry.low, runtime.telemetry.mid, runtime.telemetry.high];
    for (let index = 0; index < 64; index++) {
      const band = bands[Math.min(2, Math.floor(index / 22))];
      const value = Math.max(0.02, band * (0.4 + Math.sin(index * 2.17) * 0.25));
      const x = index / 63 * width;
      const y = height - value * height * 0.9;
      if (index === 0) context.moveTo(x, y); else context.lineTo(x, y);
    }
  } else if (kind === "composite") {
    for (let x = 0; x <= width; x += 2) {
      const t = x / width;
      let yv = 0.58;
      if (t < 0.08) yv = 0.88;
      else if (t < 0.14) yv = 0.67;
      else if (t < 0.21) yv = 0.64 + Math.sin(t * 850) * 0.13;
      else yv = 0.46 + Math.sin(t * 22 + runtime.telemetry.sampleTime * 0.0002) * 0.14 + Math.sin(t * 110) * 0.045;
      const y = yv * height;
      if (x === 0) context.moveTo(x, y); else context.lineTo(x, y);
    }
  } else {
    for (let x = 0; x <= width; x += 2) {
      const t = x / width;
      const amp = 0.04 + runtime.telemetry.rms * 0.35;
      const y = height * (0.5 + Math.sin(t * Math.PI * 12 + runtime.telemetry.sampleTime * 0.001) * amp * (0.7 + Math.sin(t * 31) * 0.3));
      if (x === 0) context.moveTo(x, y); else context.lineTo(x, y);
    }
  }
  context.stroke();
}

function ScopeCanvas({ runtime, kind, label }: { runtime: RuntimeState; kind: "wave" | "spectrum" | "composite"; label: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => { if (ref.current) drawScope(ref.current, runtime, kind); }, [runtime, kind]);
  const description = kind === "composite"
    ? `${label}. Generated timing diagnostic, not captured composite samples.`
    : `${label}. Telemetry-derived diagnostic. RMS ${Math.round(runtime.telemetry.rms * 100)} percent; peak ${Math.round(runtime.telemetry.peak * 100)} percent.`;
  return <canvas ref={ref} className="scope-canvas" role="img" aria-label={description} />;
}

export function ScopesPanel({ runtime, onCanvases }: { runtime: RuntimeState; onCanvases?: (canvases: ScopeCanvases) => void }) {
  const oscilloscope = useRef<HTMLCanvasElement>(null);
  const spectrum = useRef<HTMLCanvasElement>(null);
  const image = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!onCanvases || !oscilloscope.current || !spectrum.current || !image.current) return;
    onCanvases({ oscilloscope: oscilloscope.current, spectrum: spectrum.current, image: image.current });
    return () => onCanvases({});
  }, [onCanvases]);

  return (
    <div className="scope-grid">
      <section className="module"><header><span>AUDIO WAVEFORM</span><small>POST-LIMITER PCM · 2048 SAMPLES</small></header><canvas ref={oscilloscope} className="scope-canvas" role="img" aria-label={`Live post-limiter audio oscilloscope. RMS ${Math.round(runtime.telemetry.rms * 100)} percent; peak ${Math.round(runtime.telemetry.peak * 100)} percent.`} /><Meter label="RMS" value={runtime.telemetry.rms} peak={runtime.telemetry.peak} /></section>
      <section className="module"><header><span>SPECTRUM</span><small>LIVE 2048 FFT</small></header><canvas ref={spectrum} className="scope-canvas" role="img" aria-label="Live post-limiter audio spectrum" /><div className="band-readout"><span>LOW {Math.round(runtime.telemetry.low * 100)}</span><span>MID {Math.round(runtime.telemetry.mid * 100)}</span><span>HIGH {Math.round(runtime.telemetry.high * 100)}</span></div></section>
      <section className="module module--wide"><header><span>IMAGE LUMA / CHROMA</span><small>LIVE DOWNSAMPLED OUTPUT</small></header><canvas ref={image} className="scope-canvas" role="img" aria-label="Live image-space luma and chroma scope" /></section>
    </div>
  );
}

export function CompositePanel({ patch, runtime, onPatch }: { patch: CRTVarPatch; runtime: RuntimeState; onPatch: (updater: (patch: CRTVarPatch) => CRTVarPatch) => void }) {
  return (
    <div className="panel-grid composite-lab">
      <section className="module module--wide"><header><span>NTSC-M TIMING DIAGNOSTIC</span><small>GENERATED GUIDE · MODEL {patch.engine.modelRevision.toUpperCase()}</small></header><ScopeCanvas runtime={runtime} kind="composite" label={`Generated NTSC timing guide; selected reference line ${patch.video.scopeLine}`} /><div className="wave-regions"><span>SYNC TIP</span><span>BACK PORCH</span><span>COLOR BURST</span><span>ACTIVE VIDEO · REFERENCE LINE {patch.video.scopeLine}</span></div><input aria-label="Reference line selector" type="range" min={0} max={524} value={patch.video.scopeLine} onChange={(event) => onPatch((next) => ({ ...next, video: { ...next.video, scopeLine: Number(event.currentTarget.value) } }))} /><div className="receiver-note">Timing illustration only; use exported golden fixtures for bit-exact encoded and interfered samples.</div></section>
      <section className="module"><header><span>CANONICAL TIMING</span><small>RATIONAL CLOCKS</small></header><dl className="signal-spec"><div><dt>Fsc</dt><dd>315/88 MHz</dd></div><div><dt>Sample clock</dt><dd>315/22 MHz</dd></div><div><dt>Line</dt><dd>910 samples</dd></div><div><dt>Frame</dt><dd>525 lines</dd></div><div><dt>Fields</dt><dd>60000/1001 Hz</dd></div><div><dt>Storage</dt><dd>signed 16-bit</dd></div></dl></section>
      <section className="module"><header><span>RECEIVER STATE</span><small>SIMPLIFIED PLL / AGC</small></header><Meter label="LOCK" value={runtime.compositeLocked ? 0.91 : 0.18} /><Meter label="BURST" value={patch.video.burstAmplitude / 1.5} /><Meter label="TRACK" value={patch.video.receiverTracking} /><div className="receiver-note">Fixed windows · deterministic drift · YIQ FIR separation</div></section>
    </div>
  );
}
